/**
 * Keyboard Shortcuts Composable
 *
 * Provides a comprehensive system for managing keyboard shortcuts and navigation
 * throughout the application. Supports standard accessibility keyboard patterns
 * and custom application-specific shortcuts.
 */

import { ref, onMounted, onUnmounted, type Ref } from 'vue';
import { useFocusManagement } from './useFocusManagement';

export interface KeyboardShortcut {
  /** Unique identifier for the shortcut */
  id: string;

  /** Primary key or key combination */
  key: string | string[];

  /** Whether Ctrl/Cmd key must be pressed */
  ctrlKey?: boolean;

  /** Whether Shift key must be pressed */
  shiftKey?: boolean;

  /** Whether Alt/Option key must be pressed */
  altKey?: boolean;

  /** Whether Meta/Windows key must be pressed */
  metaKey?: boolean;

  /** Human-readable description of the shortcut */
  description: string;

  /** Callback function when shortcut is activated */
  handler: (event: KeyboardEvent) => void;

  /** Whether the shortcut should prevent default browser behavior */
  preventDefault?: boolean;

  /** Whether the shortcut should stop event propagation */
  stopPropagation?: boolean;

  /** Whether the shortcut is currently enabled */
  enabled?: boolean;

  /** Context where this shortcut is active (optional for global shortcuts) */
  context?: string;

  /** Priority level (higher numbers have priority) */
  priority?: number;

  /** Whether this shortcut should work even when user is typing in form fields */
  allowInFormFields?: boolean;
}

export interface KeyboardShortcutsOptions {
  /** Whether to enable default accessibility shortcuts */
  enableDefaultShortcuts?: boolean;

  /** Whether to enable debug logging */
  debug?: boolean;

  /** Global context name */
  globalContext?: string;
}

export const useKeyboardShortcuts = (options: KeyboardShortcutsOptions = {}) => {
  const { enableDefaultShortcuts = true, debug = false, globalContext = 'global' } = options;

  const focusManagement = useFocusManagement();

  // State
  const shortcuts = ref<Map<string, KeyboardShortcut>>(new Map());
  const activeContext = ref<string>(globalContext);
  const isEnabled = ref(true);

  /**
   * Normalize key string for consistent matching
   */
  const normalizeKey = (key: string): string => {
    return key.toLowerCase().replace(/\s+/g, '');
  };

  /**
   * Create a unique key for shortcut lookup
   */
  const createShortcutKey = (
    shortcut: Omit<KeyboardShortcut, 'id' | 'description' | 'handler'>
  ): string => {
    const keys = Array.isArray(shortcut.key) ? shortcut.key : [shortcut.key];
    const normalizedKeys = keys.map(normalizeKey).sort().join('+');
    const modifiers = [
      shortcut.ctrlKey && 'ctrl',
      shortcut.shiftKey && 'shift',
      shortcut.altKey && 'alt',
      shortcut.metaKey && 'meta',
    ]
      .filter(Boolean)
      .join('+');

    return `${modifiers ? modifiers + '+' : ''}${normalizedKeys}`;
  };

  /**
   * Check if event matches shortcut definition
   */
  const matchesShortcut = (event: KeyboardEvent, shortcut: KeyboardShortcut): boolean => {
    const keys = Array.isArray(shortcut.key) ? shortcut.key : [shortcut.key];
    const eventKeyNormalized = normalizeKey(event.key);

    // Check if key matches
    const keyMatches = keys.some(key => normalizeKey(key) === eventKeyNormalized);
    if (!keyMatches) return false;

    // Check modifier keys
    if (!!shortcut.ctrlKey !== (event.ctrlKey || event.metaKey)) return false;
    if (!!shortcut.shiftKey !== event.shiftKey) return false;
    if (!!shortcut.altKey !== event.altKey) return false;
    if (!!shortcut.metaKey !== event.metaKey) return false;

    return true;
  };

  /**
   * Register a keyboard shortcut
   */
  const registerShortcut = (shortcut: KeyboardShortcut): string => {
    const fullShortcut: KeyboardShortcut = {
      preventDefault: true,
      stopPropagation: false,
      enabled: true,
      context: globalContext,
      priority: 0,
      allowInFormFields: false,
      ...shortcut,
    };

    shortcuts.value.set(shortcut.id, fullShortcut);

    if (debug) {
      console.log(`Registered shortcut: ${shortcut.id} - ${shortcut.description}`);
    }

    return shortcut.id;
  };

  /**
   * Unregister a keyboard shortcut
   */
  const unregisterShortcut = (id: string): boolean => {
    const existed = shortcuts.value.has(id);
    shortcuts.value.delete(id);

    if (debug && existed) {
      console.log(`Unregistered shortcut: ${id}`);
    }

    return existed;
  };

  /**
   * Enable or disable a specific shortcut
   */
  const toggleShortcut = (id: string, enabled?: boolean): boolean => {
    const shortcut = shortcuts.value.get(id);
    if (!shortcut) return false;

    shortcut.enabled = enabled !== undefined ? enabled : !shortcut.enabled;
    return true;
  };

  /**
   * Set the active context
   */
  const setContext = (context: string): void => {
    activeContext.value = context;

    if (debug) {
      console.log(`Context changed to: ${context}`);
    }
  };

  /**
   * Get shortcuts for current context
   */
  const getActiveShortcuts = (): KeyboardShortcut[] => {
    return Array.from(shortcuts.value.values())
      .filter(
        shortcut =>
          shortcut.enabled &&
          (shortcut.context === globalContext || shortcut.context === activeContext.value)
      )
      .sort((a, b) => (b.priority || 0) - (a.priority || 0));
  };

  /**
   * Check if the user is currently typing in a form field
   */
  const isTypingInFormField = (event: KeyboardEvent): boolean => {
    const target = event.target as HTMLElement;
    if (!target || !target.tagName) return false;

    const tagName = target.tagName.toLowerCase();
    const isFormElement = tagName === 'input' || tagName === 'textarea' || tagName === 'select';

    const isContentEditable = target.contentEditable === 'true';
    const hasRole = target.getAttribute?.('role');
    const isEditableRole =
      hasRole === 'textbox' || hasRole === 'searchbox' || hasRole === 'combobox';

    return isFormElement || isContentEditable || isEditableRole;
  };

  /**
   * Handle keyboard events
   */
  const handleKeyDown = (event: KeyboardEvent): void => {
    if (!isEnabled.value) return;

    const activeShortcuts = getActiveShortcuts();

    for (const shortcut of activeShortcuts) {
      if (matchesShortcut(event, shortcut)) {
        // Skip shortcuts that should not interfere with form input
        // unless they explicitly allow it
        if (isTypingInFormField(event) && !shortcut.allowInFormFields) {
          continue;
        }

        if (debug) {
          console.log(`Executing shortcut: ${shortcut.id}`);
        }

        if (shortcut.preventDefault) {
          event.preventDefault();
        }

        if (shortcut.stopPropagation) {
          event.stopPropagation();
        }

        shortcut.handler(event);
        break; // Execute only the highest priority matching shortcut
      }
    }
  };

  /**
   * Register default accessibility shortcuts
   */
  const registerDefaultShortcuts = (): void => {
    // Navigation shortcuts
    registerShortcut({
      id: 'focus-next',
      key: 'Tab',
      description: 'Navigate to next focusable element',
      handler: event => {
        if (!event.shiftKey) {
          // Let browser handle normal tab navigation
          return;
        }
      },
      preventDefault: false,
    });

    registerShortcut({
      id: 'focus-previous',
      key: 'Tab',
      shiftKey: true,
      description: 'Navigate to previous focusable element',
      handler: () => {
        // Let browser handle shift+tab navigation
      },
      preventDefault: false,
    });

    // Application shortcuts
    registerShortcut({
      id: 'escape',
      key: 'Escape',
      description: 'Cancel current action or close modal',
      allowInFormFields: true, // Allow Escape to work in form fields
      handler: () => {
        // Emit escape event for components to handle
        document.dispatchEvent(new CustomEvent('keyboard-escape'));
      },
    });

    registerShortcut({
      id: 'home',
      key: 'Home',
      description: 'Jump to first element',
      handler: () => {
        focusManagement.focusFirst();
      },
    });

    registerShortcut({
      id: 'end',
      key: 'End',
      description: 'Jump to last element',
      handler: () => {
        focusManagement.focusLast();
      },
    });

    // Arrow key navigation (for custom components)
    registerShortcut({
      id: 'arrow-up',
      key: 'ArrowUp',
      description: 'Navigate up in custom components',
      handler: () => {
        document.dispatchEvent(new CustomEvent('keyboard-arrow', { detail: 'up' }));
      },
    });

    registerShortcut({
      id: 'arrow-down',
      key: 'ArrowDown',
      description: 'Navigate down in custom components',
      handler: () => {
        document.dispatchEvent(new CustomEvent('keyboard-arrow', { detail: 'down' }));
      },
    });

    registerShortcut({
      id: 'arrow-left',
      key: 'ArrowLeft',
      description: 'Navigate left in custom components',
      handler: () => {
        document.dispatchEvent(new CustomEvent('keyboard-arrow', { detail: 'left' }));
      },
    });

    registerShortcut({
      id: 'arrow-right',
      key: 'ArrowRight',
      description: 'Navigate right in custom components',
      handler: () => {
        document.dispatchEvent(new CustomEvent('keyboard-arrow', { detail: 'right' }));
      },
    });

    // Activation shortcuts
    registerShortcut({
      id: 'space-activate',
      key: ' ',
      description: 'Activate focused element (Space)',
      handler: event => {
        const target = event.target as HTMLElement;
        if (target && (target.tagName === 'BUTTON' || target.getAttribute('role') === 'button')) {
          target.click();
        }
      },
      preventDefault: false, // Let browser handle default behavior
    });

    registerShortcut({
      id: 'enter-activate',
      key: 'Enter',
      description: 'Activate focused element (Enter)',
      allowInFormFields: true, // Allow Enter to work in form fields
      handler: event => {
        const target = event.target as HTMLElement;
        if (target && (target.tagName === 'BUTTON' || target.getAttribute('role') === 'button')) {
          target.click();
        }
      },
      preventDefault: false, // Let browser handle default behavior
    });

    if (debug) {
      console.log('Default accessibility shortcuts registered');
    }
  };

  /**
   * Enable/disable the entire shortcuts system
   */
  const enable = (): void => {
    isEnabled.value = true;
  };

  const disable = (): void => {
    isEnabled.value = false;
  };

  /**
   * Get all registered shortcuts
   */
  const getAllShortcuts = (): KeyboardShortcut[] => {
    return Array.from(shortcuts.value.values());
  };

  /**
   * Get shortcuts by context
   */
  const getShortcutsByContext = (context: string): KeyboardShortcut[] => {
    return Array.from(shortcuts.value.values()).filter(shortcut => shortcut.context === context);
  };

  /**
   * Clear all shortcuts
   */
  const clearAllShortcuts = (): void => {
    shortcuts.value.clear();
  };

  // Setup event listeners
  onMounted(() => {
    document.addEventListener('keydown', handleKeyDown, true);

    if (enableDefaultShortcuts) {
      registerDefaultShortcuts();
    }

    if (debug) {
      console.log('Keyboard shortcuts system initialized');
    }
  });

  onUnmounted(() => {
    document.removeEventListener('keydown', handleKeyDown, true);

    if (debug) {
      console.log('Keyboard shortcuts system destroyed');
    }
  });

  return {
    // State
    activeContext,
    isEnabled,

    // Core methods
    registerShortcut,
    unregisterShortcut,
    toggleShortcut,

    // Context management
    setContext,

    // System control
    enable,
    disable,

    // Query methods
    getAllShortcuts,
    getActiveShortcuts,
    getShortcutsByContext,
    clearAllShortcuts,
  };
};

export default useKeyboardShortcuts;
